import _ from 'underscore';
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Checkbox, Stack } from '@bingads-webui-react/fabric-control';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { CustomDropdown } from '../custom-dropdown-component';
import { getAssetSourceFilterStyle } from './style';

const stackTokens = { childrenGap: 6, padding: '12px 0 0 0' };

const AssetSourceFilterInternal = ({
  defaultSelectedAssetSources,
  assetSourceOptions,
  dropdownWidth,
  onChange,
  onDismiss,
  focused,
  i18n,
  classes,
}) => {
  //
  const [selectedAssetSources, setSelectedAssetSources] = useState(defaultSelectedAssetSources);

  const selectedOptions = useMemo(
    () => _.filter(assetSourceOptions, ({ key }) => _.contains(selectedAssetSources, key)) || [],
    [selectedAssetSources, assetSourceOptions]
  );

  const onChangeCallback = useCallback((key, checked) => {
    const selecteSources = checked ? _.sortBy([...selectedAssetSources, key]) : _.without(selectedAssetSources, key);
    setSelectedAssetSources(selecteSources || []);
  }, [selectedAssetSources]);

  // the string shown on dropdown title
  const dropdownTitle = useMemo(() => {
    if (_.isEmpty(selectedOptions)) return null;
    const title = _.uniq(selectedOptions.map(({ text }) => text)).join('; ');
    return title;
  }, [selectedOptions]);

  // when the X button on dropdown title is clicked
  const onDropdownClear = useCallback((isDropdownOpen) => {
    setSelectedAssetSources([]);
    if (!isDropdownOpen && onChange && !_.isEmpty(defaultSelectedAssetSources)) {
      onChange([]);
    }
  }, [defaultSelectedAssetSources, onChange]);

  // when dropdown list is dismissed
  const onDropdownDismiss = useCallback(() => {
    if (onChange && !_.isEqual(defaultSelectedAssetSources, selectedAssetSources)) {
      onChange(selectedAssetSources, dropdownTitle);
    }
    if (onDismiss) {
      onDismiss(selectedAssetSources, dropdownTitle);
    }
  }, [defaultSelectedAssetSources, dropdownTitle, selectedAssetSources, onChange, onDismiss]);

  return (
    <CustomDropdown
      title={dropdownTitle}
      placeholder={i18n.getString(_TL_('Source'))}
      dropdownWidth={dropdownWidth}
      onClear={onDropdownClear}
      onDismiss={onDropdownDismiss}
      focused={focused}
    >
      <div className={classNames(classes.importSourceFilterDowndown, 'dropdown-asset-source-choices')}>
        <div className="dropdown-title">{i18n.getString(_TL_('Source'))}</div>
        <Stack tokens={stackTokens}>
          {assetSourceOptions.map(({ key, title }) => (
            <Checkbox
              key={key}
              label={title}
              className="dropdown-asset-source-option"
              checked={_.contains(selectedAssetSources, key)}
              onChange={(evt, checked) => onChangeCallback(key, checked, evt)}
            />
          ))}
        </Stack>
      </div>
    </CustomDropdown>
  );
};

AssetSourceFilterInternal.propTypes = {
  defaultSelectedAssetSources: PropTypes.arrayOf(PropTypes.string),
  assetSourceOptions: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    text: PropTypes.string,
    title: PropTypes.string,
  })).isRequired,
  onChange: PropTypes.func,
  onDismiss: PropTypes.func,
  focused: PropTypes.bool,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  dropdownWidth: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  classes: PropTypes.shape({
    importSourceFilterDowndown: PropTypes.string,
  }).isRequired,
};

AssetSourceFilterInternal.defaultProps = {
  defaultSelectedAssetSources: [],
  onChange: undefined,
  onDismiss: undefined,
  focused: false,
  dropdownWidth: 0,
};

export const AssetSourceFilter
  = withDefaultStyles(AssetSourceFilterInternal, getAssetSourceFilterStyle);
